import { useEffect, useState } from 'react'
import { Icon } from '../primitives/Icon'

const BANNER_HEIGHT = 36

/**
 * Fixed top banner shown when the browser goes offline.
 *
 * - Listens to `window.online` / `window.offline` events plus the initial
 *   `navigator.onLine` value.
 * - 220ms slide-down on enter; removed instantly on reconnect so the UI
 *   doesn't linger with stale messaging.
 * - Position: fixed, above the TopBar (z-index 9999 > TopBar 20), not part of
 *   document flow — so content is never pushed down.
 * - Respects `prefers-reduced-motion`: no animation when motion is reduced.
 */
export function OfflineBanner(): JSX.Element | null {
  const [offline, setOffline] = useState<boolean>(!navigator.onLine)
  const [visible, setVisible] = useState<boolean>(!navigator.onLine)

  useEffect(() => {
    function handleOffline(): void {
      setOffline(true)
      // Trigger on next frame so the initial render is at translateY(-100%)
      // and the transition has somewhere to animate toward.
      requestAnimationFrame(() => setVisible(true))
    }

    function handleOnline(): void {
      setVisible(false)
      setOffline(false)
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  if (!offline) return null

  return (
    <>
      <style>{`
        @keyframes lms-offline-slide-down {
          from { transform: translateY(-100%); }
          to   { transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .lms-offline-banner {
            animation: none !important;
          }
        }
      `}</style>
      <div
        role="status"
        aria-live="polite"
        aria-label="Connection status"
        data-testid="offline-banner"
        className="lms-offline-banner"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: BANNER_HEIGHT,
          background: 'var(--warning)',
          color: 'var(--text)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          fontSize: 13,
          fontFamily: 'var(--font-sans)',
          fontWeight: 500,
          zIndex: 9999,
          // Only animate when visible flag is true (set on next frame after
          // offline event) so we always slide in, never jump in from initial SSR.
          animation: visible
            ? 'lms-offline-slide-down var(--motion-duration-slow, 220ms) var(--motion-ease-out, ease-out) both'
            : undefined,
        }}
      >
        <Icon name="wifi_off" size={16} aria-hidden />
        <span>
          You're offline. Some actions won't work until you reconnect.
        </span>
      </div>
    </>
  )
}
